// addAppPreviewRefresh.js — THE BUILDER'S PREVIEW SHOWS WHAT THE AI JUST WROTE
// (persona round 3, B5: priya — "the preview said 'All stocked up!' although
// the AI had saved 3 sample records; only a page reload showed them"; nino —
// "added 3 sample tasks", none shown until the next change).
//
// Two causes, both in the shell:
//   1. A built app's reads are declarative `fetch:` calls rewritten to
//      `workspaceProject.<entity>` (functions/index.js _rewriteFetchDecls), and
//      @symbo.ls/fetch caches every read for 60 s by default in the page's ONE
//      query store (the in-shell preview shares it). A mount inside that time
//      writes the cached rows into state and sends no request — so a re-render
//      right after a write shows the rows from BEFORE the write.
//   2. A write the server runs on its own — a record the AI saves
//      (create/update/delete_workspace_record) or a change to the app's files
//      (commit_project_changes) — has no render step: the preview kept the
//      app it mounted before.
//
// So on every SUCCESSFUL app write of a builder turn (simoneAiTransport.js
// onToolEvent, the `completed` frame of the add-app scope):
//   - the built apps' cached reads are dropped (`removeQueries`), always;
//   - a build write (create_extension / create_app / update_app) renders next
//     on its own (the server's clientAfterExecute render) — it now reads
//     fresh rows;
//   - any other write re-renders the app bound to the builder, from the
//     server's copy of its source (sdk.ai.listExtensions), so the preview
//     shows the app as it is now and reads its rows again.
//
// FRANK: every export is self-contained (FA204) — no import, no module
// constant; siblings through `this.call`.

// The tools whose success changes what the builder's preview shows → 'render'
// (the server renders the app next), 'refresh' (nothing renders: re-render
// here) or '' (not an app write).
export function addAppPreviewWriteKind(tool) {
  const name = String(tool || '').replace(/^module_fn_/, '')
  if (name === 'create_extension' || name === 'create_app' || name === 'update_app') {
    return 'render'
  }
  if (
    name === 'create_workspace_record' ||
    name === 'update_workspace_record' ||
    name === 'delete_workspace_record' ||
    name === 'commit_project_changes'
  ) {
    return 'refresh'
  }
  return ''
}

// Drop every cached read of a built app (`workspaceProject.<entity>:…` — the
// only `from` a built app's fetch carries), so the next mount asks the server.
// → true when the cache could be reached.
export function addAppPreviewForget() {
  const el = this
  const fns = (el && el.context && el.context.functions) || {}
  if (typeof fns.removeQueries !== 'function') return false
  el.call('removeQueries', /^workspaceProject\./)
  return true
}

// After a successful app write of the builder's turn (`tool`: the tool's
// name). → Promise<'render' | 'refresh' | ''> — what was done.
export async function addAppPreviewAfterWrite(tool) {
  const el = this
  const kind = el.call('addAppPreviewWriteKind', tool)
  if (!kind) return ''
  el.call('addAppPreviewForget')
  if (kind === 'render') return 'render'
  const done = await el.call('addAppPreviewRefresh')
  return done ? 'refresh' : ''
}

// Re-render the app bound to the builder (`root._addAppProjectId`) from the
// server's copy — its source, or its pages (a multi-page app) — into the
// builder's own render host (`window.__addAppHost`, pages/add-app.js
// AppRender). The list read also refreshes the sidebar's rows (`_userApps`).
// Nothing when no app is bound or no builder is on the page. → Promise<boolean>
export async function addAppPreviewRefresh() {
  const el = this
  const root = el && el.state && el.state.root
  const id = root && root._addAppProjectId ? String(root._addAppProjectId) : ''
  const host = typeof window !== 'undefined' ? window.__addAppHost : null
  if (!id || !host || typeof host.call !== 'function') return false
  const fns = (el.context && el.context.functions) || {}
  const ai = typeof fns.getAiService === 'function' ? el.call('getAiService') : null
  if (!ai || typeof ai.listExtensions !== 'function') return false
  let list
  try {
    const wsId =
      typeof fns.activeWorkspaceId === 'function' ? el.call('activeWorkspaceId', root) : null
    list = await ai.listExtensions(wsId ? { workspaceId: wsId } : {})
  } catch (err) {
    console.warn('[add-app] the preview could not read the app again:', err && err.message)
    return false
  }
  const apps = Array.isArray(list) ? list : []
  // the person may have opened another app while the list was read
  if (String(root._addAppProjectId || '') !== id) return false
  const ext = apps.find((a) => a && String(a.id) === id)
  if (!ext) return false
  if (typeof root.update === 'function') root.update({ _userApps: apps })
  el.call('addAppPreviewForget')
  if (ext.appKind === 'multipage' && Array.isArray(ext.pages) && ext.pages.length) {
    await host.call('renderMultiPageApp', {
      name: ext.name,
      pages: ext.pages,
      theme: ext.theme || null
    })
    return true
  }
  if (ext.source) {
    await host.call('injectPageInApp', { name: ext.name, source: ext.source })
    return true
  }
  return false
}
